import { action, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { getStorage, removeStorage, saveLocalStorage, saveToSession } from '@/utils/browsers';
import { AuthApi } from '@/apis';
import { ekDomain, isPathNoLogin } from '@/configs/constants';
import { Profile } from '@/types/auth';
import { ResponseData } from '@/types/http';
import {
    ChangePasswordCMSPostRequest,
    ChangePasswordEnduserPostRequest,
    ConfirmEmailPostRequest,
    ConfirmEmailPostResponse,
    LoginPostResponse,
    SetPasswordPostRequest
} from '@/types/http-payload/auth';
import { COMMON_TYPE, FORM_TYPE, FormType } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { URL_CONFIG } from '@/configs/environment';

interface OptionClearAuth {
    isLogout?: boolean
}

export default class AuthStore extends BaseStore {
    api: AuthApi;

    profileGas?: Profile = getStorage('profile_gas', true);
    tokenGas: string = getStorage('token_gas');

    profileEk?: Profile = getStorage('profile_ek', true);
    tokenEk: string = getStorage('token_ek');

    modeMyAccountForm: FormType = FORM_TYPE.View;

    // register ek
    isCompleteTestEK: boolean = false;
    answersTest: any = undefined;
    showAnswer: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            profileGas: observable,
            profileEk: observable,
            tokenGas: observable,
            tokenEk: observable,
            // register ek
            isCompleteTestEK: observable,
            answersTest: observable,
            showAnswer: observable,
            //
            modeMyAccountForm: observable,
            signIn: flow.bound,
            confirmEmailForgetPassword: flow.bound,
            confirmEmailRegister: flow.bound,
            registerRD: flow.bound,
            registerEK: flow.bound,
            uploadOcrNoToken: flow.bound,
            setPassword: flow.bound,
            changePasswordEnduser: flow.bound,
            changePasswordCMS: flow.bound,
            resetPasswordCMS: flow.bound,
            checkUuid: flow.bound,
            verifyToken: flow.bound,
            logout: flow.bound,
            getProfile: action.bound,
            // change email
            changeEmailEnduser: flow.bound,
            clearAuthentication: action.bound
        });
        this.api = new AuthApi();
    }

    *signIn(payload) {
        const { keepme, ...rest } = payload;
        try {
            const res: ResponseData<LoginPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.login, rest);
            if (!res?.ok) return false;

            const { token, user } = res.data;

            if (user.type === COMMON_TYPE.GAS) {
                this.tokenGas = token;
                this.profileGas = user;

                if (keepme) {
                    saveLocalStorage('token_gas', token);
                    saveLocalStorage('profile_gas', JSON.stringify(user));
                } else {
                    saveToSession('token_gas', token);
                    saveToSession('profile_gas', user);
                }
            } else if (user.type === COMMON_TYPE.EK) {
                this.tokenEk = token;
                this.profileEk = user;

                if (keepme) {
                    saveLocalStorage('token_ek', token);
                    saveLocalStorage('profile_ek', JSON.stringify(user));
                } else {
                    saveToSession('token_ek', token);
                    saveToSession('profile_ek', user);
                }
            }

            return res.data;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *confirmEmailForgetPassword(payload: ConfirmEmailPostRequest) {
        try {
            const res: ResponseData<ConfirmEmailPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.confirmEmailForgetPassword, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    // #region Register Enduser
    *confirmEmailRegister(payload: ConfirmEmailPostRequest) {
        try {
            const res: ResponseData<ConfirmEmailPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.confirmEmailRegister, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *registerRD(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerRD, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *registerEK(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerEK, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *uploadOcrNoToken(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.uploadOcrNoToken, payload);
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.error(error);
        }
    }
    // #endregion Register Enduser

    *setPassword(payload: SetPasswordPostRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.confirmPassword, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *changePasswordEnduser(payload: ChangePasswordEnduserPostRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changePasswordEnduser, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *changePasswordCMS(payload: ChangePasswordCMSPostRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changePasswordCMS, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *resetPasswordCMS(id: number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.resetPasswordCMS, id);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    getProfile() {
        if (isPathEk(window.location.pathname)) {
            const res: Profile = getStorage('user_ek', true);
            if (res) {
                this.profileEk = res;
            }
        } else {
            const res: Profile = getStorage('user_gas', true);
            if (res) {
                this.profileGas = res;
            }
        }
    }

    *checkUuid(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.checkUuid, payload);
            if (res?.ok) {
                return res;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    // #region Change email enduser
    *changeEmailEnduser(payload: { id: number, email: string }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changeEmailEnduser, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *verifyToken() {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.verifyToken);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    *logout() {
        try {
            yield this.rootStore.apiStore.call(this.api, this.api.logout);
            this.clearAuthentication({ isLogout: true });
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    clearAuthentication(options?: OptionClearAuth) {
        const pathname = window.location.pathname;
        const isEk = isPathEk(pathname);
        if (isPathEk(pathname)) {
            removeStorage(['token_ek', 'profile_ek']);
            this.tokenEk = '';
            this.profileEk = undefined;
        } else {
            removeStorage(['token_gas', 'profile_gas']);
            this.tokenGas = '';
            this.profileGas = undefined;
        }

        let urlReturn = '';

        if (isEk) {
            urlReturn = `${URL_CONFIG.APP.url}/${ekDomain}`;
        } else {
            urlReturn = `${URL_CONFIG.DOMAIN_WORDPRESS.url}wp-content/themes/bestbike/logout.php`;
        }

        if (options?.isLogout) {
            window.location.href = urlReturn;
            return;
        }

        if (!isPathNoLogin(pathname)) {
            window.location.href = urlReturn;
        }
        // if (options?.isLogout) {
        //     window.location.href = `${URL_CONFIG.DOMAIN_WORDPRESS.url}wp-content/themes/bestbike/logout.php`;
        //     return;
        // }

        // if (!isPathNoLogin(pathname)) {
        //     window.location.href = `${URL_CONFIG.DOMAIN_WORDPRESS.url}wp-content/themes/bestbike/logout.php`;
        // }
    }
}
